import { BaseApiService } from './base.service';
import type { JsonApiResponse } from './base.service';
import type {
  CreateUploadAttributes,
  UploadResource,
} from '@/types/upload-intake';

/**
 * The one way a file enters a building (docs/upload/ADR-002).
 *
 * Register → PUT the bytes → confirm → read the answer, either by listening for
 * `uploads/completed` or by polling {@link get}. Registering the same file twice
 * while its intake is unfinished resumes it rather than uploading a second copy,
 * so a retry after a dropped connection is free.
 */
export class UploadsService extends BaseApiService {
  async create(
    projectId: string,
    attributes: CreateUploadAttributes
  ): Promise<UploadResource> {
    const response = await this.post<JsonApiResponse<UploadResource>>(
      `/v1/projects/${projectId}/uploads`,
      this.formatJsonApiData('upload', { ...attributes }),
      undefined,
      { loading: { trackEndpoint: `uploads.create.${projectId}` } }
    );
    return response.data;
  }

  async confirm(
    projectId: string,
    uploadId: string
  ): Promise<UploadResource> {
    const response = await this.post<JsonApiResponse<UploadResource>>(
      `/v1/projects/${projectId}/uploads/${uploadId}/confirm`,
      undefined,
      undefined,
      { loading: { trackEndpoint: `uploads.confirm.${uploadId}` } }
    );
    return response.data;
  }

  /**
   * The polling point for a client without a socket. Re-read
   * `validation.is_in_progress`: which statuses are terminal is a server fact,
   * and a client that keeps its own copy of that list stops asking too late.
   */
  async get(projectId: string, uploadId: string): Promise<UploadResource> {
    const response = await this.httpGet<JsonApiResponse<UploadResource>>(
      `/v1/projects/${projectId}/uploads/${uploadId}`,
      undefined,
      { loading: { trackEndpoint: `uploads.show.${uploadId}` } }
    );
    return response.data;
  }

  /** Unfinished intakes — what a client resuming mid-upload needs. */
  async listPending(projectId: string): Promise<UploadResource[]> {
    const response = await this.httpGet<JsonApiResponse<UploadResource[]>>(
      `/v1/projects/${projectId}/uploads`,
      undefined,
      { loading: { trackEndpoint: `uploads.list.${projectId}` } }
    );
    return response.data;
  }

  /**
   * "Use as a view" / "It's my building": the user vouches for a file the server
   * filed as a drawing, a reference or another building. It becomes a view and
   * the reference it became is removed. 422 when the outcome was never filed
   * elsewhere.
   */
  async promote(
    projectId: string,
    uploadId: string
  ): Promise<UploadResource> {
    const response = await this.post<JsonApiResponse<UploadResource>>(
      `/v1/projects/${projectId}/uploads/${uploadId}/promote`,
      undefined,
      undefined,
      { loading: { trackEndpoint: `uploads.promote.${uploadId}` } }
    );
    return response.data;
  }
}

export const uploadsService = new UploadsService();
